// src/components/LocalCrushList.tsx
// Pre-signup crush list — no auth, no backend, no PII encryption.
// Writes to localStorage only. Replayed into the real account on first login
// via replayPresignupList.ts.
//
// TRUST BOUNDARY: public/unauth — no tokens, no mutations, no sensitive reads.

import { useState, useEffect, useRef } from "react";
import {
  FaPhone,
  FaEnvelope,
  FaInstagram,
  FaFacebook,
  FaSnapchatGhost,
} from "react-icons/fa";
import { HandleHelpModal } from "./HandleHelpModal";
import InfoModal from "./InfoModal";
import InfoIcon from "./InfoIcon";

// ---- Storage ----------------------------------------------------------------

export const LS_KEY = "crushhh_presignup_list";

type Vibe = "1" | "2" | "3";

export interface PresignupEntry {
  platform: string;
  contact: string;   // E.164 for phone, lowercase for email, profile URL for social
  vibe: Vibe;
  addedAt: number;
  verified?: boolean; // true once user completes the confirm step
  imported?: boolean; // true once replayed into authenticated vault
}

export interface PresignupStore {
  version: 1;
  entries: PresignupEntry[];
}

export function readPresignupStore(): PresignupEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed: PresignupStore = JSON.parse(raw);
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) return [];
    return parsed.entries;
  } catch {
    return [];
  }
}

function writePresignupStore(entries: PresignupEntry[]) {
  try {
    const store: PresignupStore = { version: 1, entries };
    localStorage.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    // storage quota full — non-fatal
  }
}

// ---- Helpers ----------------------------------------------------------------

function digitsOnly(s: string) {
  return s.replace(/\D/g, "");
}

function formatUSPhone(d: string) {
  const n = digitsOnly(d).slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}

function toE164(digits: string) {
  const n = digitsOnly(digits);
  return n.length === 10 ? `+1${n}` : "";
}

function formatE164ForDisplay(contact: string) {
  if (contact.startsWith("+1") && contact.length === 12) {
    const d = contact.slice(2);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return contact;
}

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

// Normalize a social handle: strip leading @, trim, lowercase, extract from URL.
// Keeps the result consistent with what the backend normalizes before hashing.
function normalizeSocialHandle(input: string): string {
  let h = input.trim().toLowerCase().replace(/^@+/, "");
  if (h.includes("/")) {
    try {
      const url = new URL(h.startsWith("http") ? h : `https://${h}`);
      const parts = url.pathname.split("/").filter(Boolean);
      h = parts[parts.length - 1] || h;
    } catch {
      h = h.split("/").filter(Boolean).pop() || h;
    }
  }
  return h;
}

// Generate the profile URL to store and open in a tab.
function socialProfileUrl(platform: string, handle: string): string {
  const h = normalizeSocialHandle(handle);
  if (!h) return "";
  switch (platform) {
    case "instagram": return `https://instagram.com/${h}`;
    case "facebook":  return `https://facebook.com/${h}`;
    case "snapchat":  return `https://snapchat.com/add/${h}`;
    default: return "";
  }
}

// Extract the handle from a stored profile URL for display.
function extractHandleForDisplay(contact: string): string {
  try {
    const url = new URL(contact);
    const parts = url.pathname.split("/").filter(Boolean);
    return `@${parts[parts.length - 1] || contact}`;
  } catch {
    return contact;
  }
}

// ---- Constants --------------------------------------------------------------

const CAP = 12;

const SOCIAL_PLATFORMS = new Set(["instagram", "facebook", "snapchat"]);

const PLATFORMS = [
  { id: "snapchat",  label: "Snapchat",  Icon: FaSnapchatGhost },
  { id: "instagram", label: "Instagram", Icon: FaInstagram },
  { id: "facebook",  label: "Facebook",  Icon: FaFacebook },
  { id: "phone",     label: "Phone",     Icon: FaPhone },
  { id: "email",     label: "Email",     Icon: FaEnvelope },
] as const;

const BRAND = {
  coral:  "#E05A3A",
  cream:  "#F0DFC8",
  ink:    "#1A1A1A",
  pageBg: "#FDF6EE",
  white:  "#FFFFFF",
} as const;

function vibeLabel(v: Vibe) {
  return v === "3" ? "❤️❤️❤️ down bad" : v === "2" ? "❤️❤️ like like" : "❤️ curious";
}

// ---- Props ------------------------------------------------------------------

interface Props {
  onEntriesChange?: (count: number) => void;
  onFirstConfirm?: () => void;
  onSubsequentConfirm?: () => void;
  onConfirmedCountChange?: (count: number) => void;
}

// ---- Component --------------------------------------------------------------

export default function LocalCrushList({ onEntriesChange, onFirstConfirm, onSubsequentConfirm, onConfirmedCountChange }: Props) {
  const [entries, setEntries] = useState<PresignupEntry[]>(() => readPresignupStore());
  const [expanded,    setExpanded]    = useState(false);
  const [platform,    setPlatform]    = useState<string | null>(null);
  const [contact,     setContact]     = useState("");
  const [vibe,        setVibe]        = useState<Vibe | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  const [addHov,      setAddHov]      = useState(false);
  const [addPress,    setAddPress]    = useState(false);
  // Session-level: which contacts have been opened in a new tab.
  const [viewedInTab, setViewedInTab] = useState<Set<string>>(new Set());
  // Session-level: which contacts skipped the tab-view via "Having trouble?".
  const [skipViewFor, setSkipViewFor] = useState<Set<string>>(new Set());
  const [helpModalEntry, setHelpModalEntry] = useState<{ entry: PresignupEntry; idx: number } | null>(null);
  const [vibeInfoOpen,  setVibeInfoOpen]  = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const lastEntryRef = useRef<HTMLLIElement | null>(null);
  const prevLengthRef = useRef<number>(entries.length);

  const hasEntries = entries.length > 0;
  const isExpanded = expanded;
  const used = entries.length;
  const full = used >= CAP;

  function isInputValid(): boolean {
    const val = contact.trim();
    if (!val || !platform) return false;
    if (platform === "phone") return digitsOnly(val).length === 10;
    if (platform === "email") return isValidEmail(val);
    return true;
  }
  const canAdd = !full && !!platform && !!vibe && isInputValid();

  useEffect(() => {
    onEntriesChange?.(entries.length);
  }, [entries.length, onEntriesChange]);

  useEffect(() => {
    if (entries.length > prevLengthRef.current) {
      setTimeout(() => {
        if (!lastEntryRef.current) return;
        const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
        const bandHeight = bandEl ? bandEl.offsetHeight : 0;
        lastEntryRef.current.style.scrollMarginTop = `${bandHeight}px`;
        lastEntryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    }
    prevLengthRef.current = entries.length;
  }, [entries.length]);

  function openExpanded() {
    setPlatform(null);
    setVibe(null);
    setContact("");
    setError(null);
    setExpanded(true);
    setTimeout(() => {
      if (!cardRef.current) return;
      const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
      const bandHeight = bandEl ? bandEl.offsetHeight : 0;
      cardRef.current.style.scrollMarginTop = `${bandHeight}px`;
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  function handlePlatformChange(id: string) {
    setPlatform(id);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 40);
  }

  function handleAdd() {
    setError(null);
    if (!platform || !vibe) return;
    let val = contact.trim();
    if (!val) return;

    if (platform === "phone") {
      const digits = digitsOnly(val);
      if (digits.length !== 10) {
        setError("Enter a valid 10-digit US phone number.");
        return;
      }
      val = toE164(digits);
    }

    if (platform === "email") {
      if (!isValidEmail(val)) {
        setError("Enter a valid email address.");
        return;
      }
      val = val.toLowerCase();
    }

    if (SOCIAL_PLATFORMS.has(platform)) {
      const url = socialProfileUrl(platform, val);
      if (!url) {
        setError("Enter a valid username or profile URL.");
        return;
      }
      val = url;
    }

    if (entries.some((e) => e.contact === val)) {
      setError("Already in your list.");
      return;
    }

    if (full) {
      setError("List full — remove one to add another.");
      return;
    }

    const entry: PresignupEntry = {
      platform,
      contact: val,
      vibe,
      addedAt: Date.now(),
      verified: false,
    };
    const next = [...entries, entry];
    setEntries(next);
    writePresignupStore(next);
    setContact("");
    setExpanded(false);
  }

  function handleRemove(idx: number) {
    const removed = entries[idx];
    const next = entries.filter((_, i) => i !== idx);
    setEntries(next);
    writePresignupStore(next);
    // Clean up session state for removed entry
    setViewedInTab((prev) => { const s = new Set(prev); s.delete(removed.contact); return s; });
    setSkipViewFor((prev) => { const s = new Set(prev); s.delete(removed.contact); return s; });
    onConfirmedCountChange?.(next.filter((e) => e.verified).length);
  }

  function handleOpenTab(entry: PresignupEntry) {
    window.open(entry.contact, "_blank", "noopener,noreferrer");
    setViewedInTab((prev) => new Set(prev).add(entry.contact));
  }

  function handleConfirmEntry(idx: number) {
    window.dispatchEvent(new CustomEvent('crushhh:crushConfirmed'));
    const confirmedBefore = entries.filter((e) => e.verified).length;
    const next = entries.map((e, i) =>
      i === idx ? { ...e, verified: true } : e,
    );
    setEntries(next);
    writePresignupStore(next);
    if (confirmedBefore === 0) {
      onFirstConfirm?.();
    } else {
      onSubsequentConfirm?.();
    }
    onConfirmedCountChange?.(next.filter((e) => e.verified).length);
  }

  // ---- Styles ---------------------------------------------------------------

  const confirmBtn: React.CSSProperties = {
    width: "100%",
    padding: "10px 20px",
    background: BRAND.coral,
    color: BRAND.white,
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: 16,
    border: `3px solid ${BRAND.ink}`,
    borderRadius: 100,
    boxShadow: `4px 4px 0 ${BRAND.ink}`,
    cursor: "pointer",
    transition: "all 0.1s ease",
    boxSizing: "border-box",
  };

  const viewBtn: React.CSSProperties = {
    ...confirmBtn,
  };

  // ---- Render ---------------------------------------------------------------

  return (
    <div
      ref={cardRef}
      style={{
        background: "#E8D4B8",
        border: `3px solid ${BRAND.ink}`,
        borderRadius: 20,
        boxShadow: `6px 6px 0px ${BRAND.ink}`,
        padding: "20px 18px 24px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      {/* Title */}

      <div
        style={{
          fontFamily: "'Fredoka', sans-serif",
          fontSize: 24,
          fontWeight: 600,
          color: BRAND.ink,
          textAlign: "center",
          marginBottom: 12,
        }}
      >
        Add Your Crush
      </div>
      {/* Counter — visible from first crush onward */}
      {used > 0 && (
        <div
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: 12,
            fontWeight: 700,
            color: "rgba(26,26,26,0.5)",
            textAlign: "center",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          {used} of {CAP}
        </div>
      )}

      {/* Collapsed bar */}
      {!isExpanded && (
        <div
          role={full ? undefined : "button"}
          tabIndex={full ? -1 : 0}
          onClick={full ? undefined : openExpanded}
          onKeyDown={full ? undefined : (e) => e.key === "Enter" && openExpanded()}
          style={{
            display: "flex",
            alignItems: "center",
            border: `2px solid ${BRAND.coral}`,
            borderRadius: 100,
            padding: "13px 20px",
            background: "#FFFFFF",
            cursor: full ? "not-allowed" : "text",
            fontFamily: "'Nunito', sans-serif",
            fontSize: 15,
            color: "rgba(26,26,26,0.4)",
            boxShadow: "none",
            userSelect: "none",
            opacity: full ? 0.45 : 1,
          }}
        >
          {full ? "List Full" : hasEntries ? "Add another crush" : "Add their handle, phone or email"}
        </div>
      )}

      {/* Handle help modal — opened from "Having trouble?" link on social entries */}
      {helpModalEntry && (
        <HandleHelpModal
          platform={helpModalEntry.entry.platform}
          handle={helpModalEntry.entry.contact}
          onConfirmed={() => {
            const currentIdx = entries.findIndex(
              (en) => en.contact === helpModalEntry.entry.contact,
            );
            setHelpModalEntry(null);
            if (currentIdx !== -1) handleConfirmEntry(currentIdx);
          }}
          onReEnter={() => setHelpModalEntry(null)}
          onDismiss={() => setHelpModalEntry(null)}
        />
      )}

      {/* Expanded form — animates collapse/expand */}
      <div
        style={{
          overflow: "hidden",
          maxHeight: isExpanded ? "600px" : "0px",
          opacity: isExpanded ? 1 : 0,
          transition: "max-height 0.3s ease, opacity 0.25s ease",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingRight: 6, paddingBottom: 6 }}>

          {/* Platform selector — icon-only square grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
            {PLATFORMS.map(({ id, label, Icon }) => {
              const active = platform === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handlePlatformChange(id)}
                  title={label}
                  aria-label={label}
                  aria-pressed={active}
                  className={`relative flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all ${
                    active
                      ? "bg-brand text-white shadow-[2px_2px_0px_#D4724A]"
                      : "bg-[#ffffff] text-[#eb6d69] hover:bg-[#D4B896] focus:outline-none focus:ring-0 active:outline-none outline-none shadow-[2px_2px_0px_#D4724A]"
                  }`}
                >
                  <Icon size={20} />
                </button>
              );
            })}
          </div>

          {/* Vibe picker */}
          <div>
            <div
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 24,
                fontWeight: 600,
                color: BRAND.ink,
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              Choose Your Vibe
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {(["1", "2", "3"] as Vibe[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVibe(v)}
                  style={{
                    border: `2px solid ${BRAND.ink}`,
                    borderRadius: 100,
                    fontFamily: "'Fredoka', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "8px 4px",
                    textAlign: "center",
                    background: vibe === v ? "#E8B89A" : BRAND.white,
                    color: BRAND.ink,
                    boxShadow: vibe === v ? `1px 1px 0 ${BRAND.ink}` : `3px 3px 0 ${BRAND.ink}`,
                    lineHeight: 1.3,
                    transition: "all 0.1s ease",
                    boxSizing: "border-box",
                  }}
                >
                  <span style={{ display: "block" }}>
                    {v === "1" ? "❤️" : v === "2" ? "❤️❤️" : "❤️❤️❤️"}
                  </span>
                  <span style={{ display: "block" }}>
                    {v === "1" ? "curious" : v === "2" ? "like like" : "down bad"}
                  </span>
                </button>
              ))}
            </div>
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                color: "rgba(26,26,26,0.45)",
                textAlign: "center",
                margin: "16px 0 0",
              }}
            >
              Your vibe never overshoots theirs.{" "}
              <button
                type="button"
                onClick={() => setVibeInfoOpen(true)}
                aria-label="How vibe matching works"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0 0 0 2px",
                  verticalAlign: "middle",
                  lineHeight: 1,
                }}
              >
                <InfoIcon size={15} />
              </button>
            </p>
            {vibeInfoOpen && (
              <InfoModal onClose={() => setVibeInfoOpen(false)}>
                <p style={{ margin: "0 0 10px" }}>
                  <strong>The reveal only shows what you both share.</strong>
                </p>
                <p style={{ margin: 0 }}>
                  Say you&apos;re down bad and they&apos;re just curious, they&apos;ll only ever see curious.{" "}
                  <strong>You&apos;ll never look more into it than they do — and they&apos;ll never look more into it than you do.</strong>{" "}
                  That&apos;s the whole point.
                </p>
              </InfoModal>
            )}
          </div>

          {/* Contact input + Add button */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              ref={inputRef}
              type={
                platform === "phone" ? "tel" :
                platform === "email" ? "email" : "text"
              }
              inputMode={platform === "phone" ? "numeric" : undefined}
              autoComplete={
                platform === "phone" ? "tel" :
                platform === "email" ? "email" : "off"
              }
              placeholder={
                platform === null        ? "Add Your Crush" :
                platform === "phone"     ? "(555) 123-4567" :
                platform === "email"     ? "their.email@example.com" :
                platform === "instagram" ? "username or instagram.com/username" :
                platform === "facebook"  ? "username or facebook.com/username" :
                                           "username or snapchat.com/add/username"
              }
              value={platform === "phone" ? formatUSPhone(contact) : contact}
              onChange={(e) => {
                setError(null);
                if (platform === "phone") {
                  setContact(digitsOnly(e.target.value).slice(0, 10));
                } else if (platform !== null && SOCIAL_PLATFORMS.has(platform)) {
                  // Lowercase on input; full normalization happens in handleAdd
                  setContact(e.target.value.toLowerCase());
                } else {
                  setContact(e.target.value);
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              disabled={full}
              style={{
                width: "100%",
                border: `2px solid ${BRAND.coral}`,
                borderRadius: 12,
                padding: "12px 16px",
                fontFamily: "'Nunito', sans-serif",
                fontSize: 15,
                background: full ? "rgba(26,26,26,0.04)" : "#FFFFFF",
                color: BRAND.ink,
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.1s ease",
              }}
            />

            {error && (
              <div
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: BRAND.coral,
                  padding: "6px 12px",
                  background: "rgba(224,90,58,0.07)",
                  border: `1.5px solid ${BRAND.coral}`,
                  borderRadius: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              onMouseEnter={() => canAdd && setAddHov(true)}
              onMouseLeave={() => { setAddHov(false); setAddPress(false); }}
              onMouseDown={() => canAdd && setAddPress(true)}
              onMouseUp={() => setAddPress(false)}
              style={{
                width: "100%",
                padding: "12px 24px",
                background: addPress ? "#B34428" : addHov ? "#C94E30" : BRAND.coral,
                color: BRAND.white,
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 700,
                fontSize: 18,
                border: `3px solid ${BRAND.ink}`,
                borderRadius: 100,
                boxShadow: `4px 4px 0 ${BRAND.ink}`,
                cursor: canAdd ? "pointer" : "not-allowed",
                opacity: canAdd ? 1 : 0.45,
                transform: addPress ? "scale(0.97)" : "scale(1)",
                transition: "all 0.1s ease",
                boxSizing: "border-box",
              }}
            >
              {full ? "List Full" : "Add Crush"}
            </button>
          </div>

        </div>
      </div>

      {/* Added entries list — stays visible when form is collapsed */}
      {hasEntries && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 17,
              fontWeight: 700,
              color: BRAND.ink,
              marginBottom: 10,
            }}
          >
            Your Crushes ({used})
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background: BRAND.white,
              borderRadius: 14,
            }}
          >
            {entries.map((e, i) => {
              const isSocial  = SOCIAL_PLATFORMS.has(e.platform);
              const viewed    = viewedInTab.has(e.contact);
              const skipping  = skipViewFor.has(e.contact);
              const canConfirm = !isSocial || viewed || skipping;

              const displayName = isSocial
                ? extractHandleForDisplay(e.contact)
                : e.platform === "phone"
                ? formatE164ForDisplay(e.contact)
                : e.contact;

              return (
                <li
                  key={i}
                  ref={i === entries.length - 1 ? lastEntryRef : null}
                  style={{
                    background: BRAND.white,
                    borderRadius: 14,
                    padding: "12px 14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {/* Header row: contact info + remove */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: 14,
                          fontWeight: 700,
                          color: BRAND.ink,
                          wordBreak: "break-all",
            
                        }}
                      >
                        {displayName}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 5,
                          marginTop: 5,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            background: BRAND.coral,
                            color: BRAND.white,
                            borderRadius: 100,
                            padding: "2px 10px",
                            fontFamily: "'Fredoka', sans-serif",
                            fontSize: 12,
                            textTransform: "capitalize",
                          }}
                        >
                          {e.platform}
                        </span>
                        <span
                          style={{
                            background: BRAND.cream,
                            color: BRAND.ink,
                            borderRadius: 100,
                            padding: "2px 10px",
                            fontFamily: "'Fredoka', sans-serif",
                            fontSize: 12,
                          }}
                        >
                          {vibeLabel(e.vibe)}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemove(i)}
                      style={{
                        background: BRAND.white,
                        color: BRAND.ink,
                        border: `2px solid ${BRAND.ink}`,
                        borderRadius: 100,
                        padding: "4px 12px",
                        fontFamily: "'Fredoka', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        flexShrink: 0,
                        boxShadow: `2px 2px 0 ${BRAND.ink}`,
                        transition: "all 0.1s ease",
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Verification actions — hidden once verified */}
                  {!e.verified && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>

                      {/* Social: view-first flow */}
                      {isSocial && !canConfirm && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenTab(e)}
                            style={viewBtn}
                          >
                            Click to View & Confirm Profile
                          </button>
                          <button
                            type="button"
                            onClick={() => setHelpModalEntry({ entry: e, idx: i })}
                            style={{
                              background: "none",
                              border: "none",
                              fontFamily: "'Nunito', sans-serif",
                              fontSize: 13,
                              fontWeight: 600,
                              color: "rgba(26,26,26,0.45)",
                              cursor: "pointer",
                              textDecoration: "underline",
                              textUnderlineOffset: 3,
                              padding: "4px 0",
                              textAlign: "center",
                            }}
                          >
                            Having trouble viewing their profile?
                          </button>
                        </>
                      )}

                      {/* Social: confirm step (after viewing or skipping) */}
                      {isSocial && canConfirm && (
                        <button
                          type="button"
                          onClick={() => handleConfirmEntry(i)}
                          style={confirmBtn}
                        >
                          ✓ Confirm Crushhh
                        </button>
                      )}

                      {/* Phone / email: one-tap confirm */}
                      {!isSocial && (
                        <button
                          type="button"
                          onClick={() => handleConfirmEntry(i)}
                          style={confirmBtn}
                        >
                          Confirm
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
